// Defines the structure and parameters for Biometric data entry and display.

export const biometricCategories = [
  {
    key: 'cognition',
    title: 'Cognition',
    parameters: [
      { key: 'tmtA_time', name: 'TMT-A Time', units: 'sec', type: 'Cognitive Test' },
      { key: 'tmtA_errors', name: 'TMT-A Errors', type: 'Cognitive Test' },
      { key: 'tmtB_time', name: 'TMT-B Time', units: 'sec', type: 'Cognitive Test' },
      { key: 'tmtB_errors', name: 'TMT-B Errors', type: 'Cognitive Test' },
      { key: 'wordRecall_immediate1', name: 'Word Recall - Immediate Trial 1', units: '/10', type: 'Cognitive Test' },
      { key: 'wordRecall_immediate2', name: 'Word Recall - Immediate Trial 2', units: '/10', type: 'Cognitive Test' },
      { key: 'wordRecall_delayed', name: 'Word Recall - Delayed', units: '/10', type: 'Cognitive Test' },
      { key: 'stroop_word_time', name: 'Stroop Word Time', units: 'sec', type: 'Cognitive Test' },
      { key: 'stroop_word_errors', name: 'Stroop Word Errors', type: 'Cognitive Test' },
      { key: 'stroop_color_time', name: 'Stroop Color Time', units: 'sec', type: 'Cognitive Test' },
      { key: 'stroop_color_errors', name: 'Stroop Color Errors', type: 'Cognitive Test' },
      { key: 'stroop_incongruent_time', name: 'Stroop Incongruent Time', units: 'sec', type: 'Cognitive Test' },
      { key: 'stroop_incongruent_errors', name: 'Stroop Incongruent Errors', type: 'Cognitive Test' },
      { key: 'stroop_interference_time', name: 'Stroop Interference Time', units: 'sec', type: 'Cognitive Test' },
      { key: 'stroop_interference_errors', name: 'Stroop Interference Errors', type: 'Cognitive Test' },
    ],
  },
  {
    key: 'vision',
    title: 'Vision',
    parameters: [
      { key: 'biometrics_vision_visualAcuity_distance_right', name: 'Visual Acuity - Distance (Right Eye)', units: 'Snellen', type: 'Biometric' },
      { key: 'biometrics_vision_visualAcuity_distance_left', name: 'Visual Acuity - Distance (Left Eye)', units: 'Snellen', type: 'Biometric' },
      { key: 'biometrics_vision_visualAcuity_distance_both', name: 'Visual Acuity - Distance (Both Eyes)', units: 'Snellen', type: 'Biometric' },
      { key: 'biometrics_vision_visualAcuity_near_right', name: 'Visual Acuity - Near (Right Eye)', units: 'Jaeger', type: 'Biometric' },
      { key: 'biometrics_vision_visualAcuity_near_left', name: 'Visual Acuity - Near (Left Eye)', units: 'Jaeger', type: 'Biometric' },
      { key: 'biometrics_vision_visualAcuity_near_both', name: 'Visual Acuity - Near (Both Eyes)', units: 'Jaeger', type: 'Biometric' },
      { key: 'biometrics_vision_visualAcuity_pinhole_right', name: 'Visual Acuity - Pinhole (Right Eye)', units: 'Snellen', type: 'Biometric' },
      { key: 'biometrics_vision_visualAcuity_pinhole_left', name: 'Visual Acuity - Pinhole (Left Eye)', units: 'Snellen', type: 'Biometric' },
    ],
  },
  {
    key: 'pulmonary',
    title: 'Pulmonary',
    parameters: [
      { key: 'fev1', name: 'FEV1', units: 'L', type: 'Spirometry' },
      { key: 'fvc', name: 'FVC', units: 'L', type: 'Spirometry' },
      { key: 'pefr', name: 'PEFR', units: 'L/min', type: 'Spirometry' },
      { key: 'fev1_predicted', name: 'FEV1 Predicted', units: 'L', type: 'Spirometry' }, // Manual Entry
      { key: 'fvc_predicted', name: 'FVC Predicted', units: 'L', type: 'Spirometry' }, // Manual Entry
      { key: 'pefr_predicted', name: 'PEFR Predicted', units: 'L/min', type: 'Spirometry' }, // Manual Entry
      { key: 'fef25_75', name: 'FEF 25-75', units: 'L/sec', type: 'Spirometry' }, // Manual Entry
      { key: 'fev1_fvc_ratio', name: 'FEV1/FVC Ratio', units: '%', type: 'Spirometry' }, // Manual Entry
      { key: 'lung_age_estimate', name: 'Lung Age Estimate', units: 'years', type: 'Spirometry' }, // Manual Entry
    ],
  },
  {
    key: 'biomeasurement',
    title: 'Biomeasurement', // Renamed from Biomeasurement and Bioimpedance
    parameters: [
      // Parameters from original Biomeasurement (redundant weight/height/BMI removed)
      { key: 'waistCircumference', name: 'Waist Circumference', units: 'cm', type: 'Biometric' },
      { key: 'hipCircumference', name: 'Hip Circumference', units: 'cm', type: 'Biometric' },
      // New parameter
      { key: 'neckCircumference', name: 'Neck circumference', units: 'cm', type: 'Biometric' }, // Added Neck circumference
      // Parameters from original Bioimpedance (redundant BMI removed, weight name clarified)
      { key: 'weight_inbody', name: 'Weight (InBody)', type: 'Biometric' },
      { key: 'skeletalMuscleMass', name: 'Skeletal Muscle Mass', type: 'Biometric' },
      { key: 'bodyFatMass', name: 'Body Fat Mass', type: 'Biometric' },
      { key: 'percentBodyFat', name: 'Percent Body Fat', type: 'Biometric' },
      { key: 'waistHipRatio', name: 'Waist-Hip Ratio', type: 'Biometric' },
      { key: 'visceralFatLevel', name: 'Visceral Fat Level', type: 'Biometric' },
      { key: 'softLeanMass', name: 'Soft Lean Mass', type: 'Biometric' },
      { key: 'upperLowerBodyBalance', name: 'Upper/Lower Body Balance', type: 'Biometric' },
      { key: 'inbodyScore', name: 'InBody Score', type: 'Biometric' },
      { key: 'bodyTypeCoordinate', name: 'Body Type Coordinate', type: 'Biometric' },
      { key: 'ree', name: 'Resting Energy Expenditure (REE)', units: 'kcal/day', type: 'Biometric', calculated: true }, // Calculated REE
      { key: 'tee', name: 'Total Energy Expenditure (TEE)', units: 'kcal/day', type: 'Biometric', calculated: true }, // Calculated TEE
    ],
  },
  {
    key: 'flexibility',
    title: 'Flexibility',
    parameters: [
      { key: 'armReach_back_right', name: 'Around Back Arm Reach (Right)', units: 'cm', type: 'Biometric' },
      { key: 'armReach_back_left', name: 'Around Back Arm Reach (Left)', units: 'cm', type: 'Biometric' },
      { key: 'legReach_sitting', name: 'Sitting Leg Reach', units: 'cm', type: 'Biometric' },
    ],
  },
  {
    key: 'balance',
    title: 'Balance',
    parameters: [
      { key: 'ybt_right_anterior_1', name: 'YBT Right Anterior 1', units: 'cm', type: 'Biometric' },
      { key: 'ybt_left_anterior_1', name: 'YBT Left Anterior 1', units: 'cm', type: 'Biometric' },
      { key: 'ybt_right_posteromedial_1', name: 'YBT Right Posteromedial 1', units: 'cm', type: 'Biometric' },
      { key: 'ybt_left_posteromedial_1', name: 'YBT Left Posteromedial 1', units: 'cm', type: 'Biometric' },
      { key: 'ybt_right_posterolateral_1', name: 'YBT Right Posterolateral 1', units: 'cm', type: 'Biometric' },
      { key: 'ybt_left_posterolateral_1', name: 'YBT Left Posterolateral 1', units: 'cm', type: 'Biometric' },
      { key: 'ybt_right_anterior_2', name: 'YBT Right Anterior 2', units: 'cm', type: 'Biometric' },
      { key: 'ybt_left_anterior_2', name: 'YBT Left Anterior 2', units: 'cm', type: 'Biometric' },
      { key: 'ybt_right_posteromedial_2', name: 'YBT Right Posteromedial 2', units: 'cm', type: 'Biometric' },
      { key: 'ybt_left_posteromedial_2', name: 'YBT Left Posteromedial 2', units: 'cm', type: 'Biometric' },
      { key: 'ybt_right_posterolateral_2', name: 'YBT Right Posterolateral 2', units: 'cm', type: 'Biometric' },
      { key: 'ybt_left_posterolateral_2', name: 'YBT Left Posterolateral 2', units: 'cm', type: 'Biometric' },
      { key: 'ybt_right_anterior_avg', name: 'YBT Right Anterior Average', units: 'cm', type: 'Biometric' },
      { key: 'ybt_left_anterior_avg', name: 'YBT Left Anterior Average', units: 'cm', type: 'Biometric' },
      { key: 'ybt_right_posteromedial_avg', name: 'YBT Right Posteromedial Average', units: 'cm', type: 'Biometric' },
      { key: 'ybt_left_posteromedial_avg', name: 'YBT Left Posteromedial Average', units: 'cm', type: 'Biometric' },
      { key: 'ybt_right_posterolateral_avg', name: 'YBT Right Posterolateral Average', units: 'cm', type: 'Biometric' },
      { key: 'ybt_left_posterolateral_avg', name: 'YBT Left Posterolateral Average', units: 'cm', type: 'Biometric' },
      { key: 'ybt_right_limb_length', name: 'YBT Right Limb Length', units: 'cm', type: 'Biometric' },
      { key: 'ybt_left_limb_length', name: 'YBT Left Limb Length', units: 'cm', type: 'Biometric' },
      { key: 'ybt_right_composite_reach', name: 'YBT Right Composite Reach', units: '%', type: 'Biometric' },
      { key: 'ybt_left_composite_reach', name: 'YBT Left Composite Reach', units: '%', type: 'Biometric' },
      { key: 'ybt_composite_deviation', name: 'YBT Composite Deviation', units: '%', type: 'Biometric' },
      { key: 'ybt_anterior_deviation', name: 'YBT Anterior Deviation', units: '%', type: 'Biometric' },
      { key: 'ybt_posteromedial_deviation', name: 'YBT Posteromedial Deviation', units: '%', type: 'Biometric' },
      { key: 'ybt_posterolateral_deviation', name: 'YBT Posterolateral Deviation', units: '%', type: 'Biometric' },
      { key: 'ybt_performance_ratio', name: 'YBT Performance Ratio', units: '%', type: 'Biometric', calculated: true },
    ],
  },
  {
    key: 'strength',
    title: 'Strength',
    parameters: [
      { key: 'handgrip_right_1', name: 'Right grip 1', units: 'kg', type: 'Biometric' },
      { key: 'handgrip_right_2', name: 'Right grip 2', units: 'kg', type: 'Biometric' },
      { key: 'handgrip_left_1', name: 'Left grip 1', units: 'kg', type: 'Biometric' },
      { key: 'handgrip_left_2', name: 'Left grip 2', units: 'kg', type: 'Biometric' },
      { key: 'handgrip_right_avg', name: 'Right grip average', units: 'kg', type: 'Biometric' },
      { key: 'handgrip_left_avg', name: 'Left grip average', units: 'kg', type: 'Biometric' },
      { key: 'handgrip_normative_status', name: 'Normative status', type: 'Biometric' },
      { key: 'handgrip_deviation_percent', name: 'Grip % deviation', units: '%', type: 'Biometric' },
      { key: 'handgrip_relative_strength', name: 'Relative grip strength', units: 'kg/kg', type: 'Biometric' },
    ],
  },
  {
    key: 'endurance',
    title: 'Endurance',
    parameters: [
      { key: 'wallsit_duration', name: 'Wall sit duration', units: 'seconds', type: 'Biometric' },
      { key: 'pushups_completed', name: 'Push ups completed', units: 'per minute', type: 'Biometric' },
      { key: 'step_test_duration', name: 'Step Test duration', units: 'minutes', type: 'Biometric' },
      { key: 'heart_rate_peak', name: 'Heart rate peak', units: 'bpm', type: 'Biometric' },
      { key: 'heart_rate_1min', name: 'Heart rate 1 minute', units: 'bpm', type: 'Biometric' },
    ],
  },
  {
    key: 'power',
    title: 'Power',
    parameters: [
      { key: 'jump_best', name: 'Best jump', units: 'cm', type: 'Biometric' },
      { key: 'jump_percent_max', name: 'Percent of max jump', units: '%', type: 'Biometric' },
      { key: 'jump_average_height', name: 'Average height', units: 'cm', type: 'Biometric' },
      { key: 'landing_impact', name: 'Landing impact', type: 'Biometric' }, // Units not specified, leaving blank for now
      { key: 'power_watts', name: 'Power', units: 'Watts', type: 'Biometric' },
    ],
  },
];

// Helper function to get parameters for a specific biometric category
export const getParametersForBiometricCategory = (categoryKey) => {
  const category = biometricCategories.find(cat => cat.key === categoryKey);
  return category ? category.parameters : [];
};

// Helper function to get all biometric parameters flattened
export const getAllBiometricParameters = () => {
  return biometricCategories.reduce((acc, category) => {
    return acc.concat(category.parameters);
  }, []);
};
